/**
 * Listado de versiones/modelos bloqueados que se consideran PRE-CLÁSICOS
 * cuando el vehículo tiene entre 25-29 años de antigüedad.
 * 
 * Fuente: "Listado bloqueo web V.5_26"
 * 
 * Reglas:
 *  - Coches 20-24 años → SIEMPRE pre-clásico
 *  - Coches 25-29 años → pre-clásico SOLO si aparecen en este listado
 *  - Coches 30+ años → SIEMPRE clásico
 *  - Motos → NUNCA pre-clásico (siempre clásico)
 */

export interface BloqueoEntry {
  marca: string;
  modelo: string;
  /** null = "Todas las versiones" (se bloquea el modelo entero) */
  versiones: string[] | null;
}

export const LISTADO_BLOQUEO_PRE_CLASICOS: BloqueoEntry[] = [
  { marca: "SEAT", modelo: "IBIZA", versiones: ["GTI", "CUPRA", "FR", "SPORT", "16V"] },
  { marca: "SEAT", modelo: "TOLEDO", versiones: ["GT", "SPORT", "16V"] },
  { marca: "PEUGEOT", modelo: "106", versiones: ["RALLYE", "GTI", "XSI", "SPORT"] },
  { marca: "PEUGEOT", modelo: "205", versiones: ["RALLYE", "XS"] },
  { marca: "PEUGEOT", modelo: "206", versiones: ["GTI", "SPORT", "XS", "XR", "CC"] },
  { marca: "PEUGEOT", modelo: "306", versiones: ["XSI", "GTI", "S16", "SPORT", "CABRIO"] },
  { marca: "PEUGEOT", modelo: "309", versiones: ["GTI", "SRD"] },
  { marca: "PEUGEOT", modelo: "405", versiones: ["MI16", "MI16X4", "GTX"] },
  { marca: "HONDA", modelo: "CIVIC", versiones: ["VTI", "VTEC", "TYPE R", "SPORT"] },
  { marca: "HONDA", modelo: "CRX", versiones: null },
  { marca: "OPEL", modelo: "ASTRA", versiones: ["GSI", "SPORT", "16V", "COUPE"] },
  { marca: "OPEL", modelo: "CALIBRA", versiones: ["16V", "TURBO"] },
  { marca: "OPEL", modelo: "CORSA", versiones: ["SPORT", "16V"] },
  { marca: "OPEL", modelo: "KADETT", versiones: ["16V", "CABRIO"] },
  { marca: "RENAULT", modelo: "CLIO", versiones: ["16V", "WILLIAMS", "SPORT", "RS"] },
  { marca: "RENAULT", modelo: "LAGUNA", versiones: null },
  { marca: "RENAULT", modelo: "MEGANE", versiones: ["COUPE", "SPORT", "16V", "RS", "CABRIO"] },
  { marca: "NISSAN", modelo: "SUNNY", versiones: ["GTI", "GTI-R", "COUPE"] },
  { marca: "NISSAN", modelo: "TERRANO", versiones: null },
  { marca: "MITSUBISHI", modelo: "MONTERO", versiones: null },
  { marca: "MITSUBISHI", modelo: "PAJERO", versiones: null },
  { marca: "LAND ROVER", modelo: "DISCOVERY", versiones: null },
  { marca: "JEEP", modelo: "CHEROKEE", versiones: null },
  { marca: "SUZUKI", modelo: "SAMURAI", versiones: null },
  { marca: "SUZUKI", modelo: "VITARA", versiones: null },
  { marca: "TOYOTA", modelo: "CELICA", versiones: null },
  { marca: "TOYOTA", modelo: "MR2", versiones: null },
  { marca: "TOYOTA", modelo: "SUPRA", versiones: null },
  { marca: "TOYOTA", modelo: "YARIS", versiones: ["TS", "SPORT"] },
  { marca: "VOLKSWAGEN", modelo: "BORA", versiones: null },
  { marca: "VOLKSWAGEN", modelo: "GOLF", versiones: ["VR6", "R32", "GTD", "16V"] },
  { marca: "VOLKSWAGEN", modelo: "VENTO", versiones: ["VR6", "GT", "SPORT"] },
  { marca: "AUDI", modelo: "A4", versiones: ["S-LINE", "QUATTRO", "CABRIO", "1.8T", "V6"] },
  { marca: "AUDI", modelo: "A8", versiones: ["QUATTRO", "V8"] },
  { marca: "CITROEN", modelo: "SAXO", versiones: ["VTS", "VTR", "16V", "SPORT"] },
  { marca: "CITROEN", modelo: "XANTIA", versiones: null },
  { marca: "CITROEN", modelo: "XSARA", versiones: ["VTS", "COUPE", "16V", "SPORT"] },
  { marca: "FORD", modelo: "ESCORT", versiones: ["XR3", "RS", "CABRIO", "COSWORTH"] },
  { marca: "FORD", modelo: "FIESTA", versiones: ["XR2", "SPORT", "ST", "3P"] },
  { marca: "FORD", modelo: "SIERRA", versiones: ["COSWORTH", "XR4", "4X4"] },
  { marca: "BMW", modelo: "SERIE 3", versiones: ["M3", "325", "328", "COUPE"] },
  { marca: "BMW", modelo: "SERIE 5", versiones: ["M5", "535", "540"] },
  { marca: "BMW", modelo: "SERIE 7", versiones: null },
];

/**
 * Normaliza un string para comparación (mayúsculas, sin acentos, sin espacios extra)
 */
function normalize(s: string): string {
  return s
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

/**
 * Resultado de la clasificación automática.
 */
export type ClasificacionConfianza = "ALTA" | "MEDIA" | "REVISAR";

export interface ClasificacionResult {
  tipo: "CLASICO" | "PRE_CLASICO";
  confianza: ClasificacionConfianza;
  razon: string;
  /** Sugerencia de la IA: qué debería comprobar el agente */
  sugerenciaRevision?: string;
}

/**
 * Comprueba si un vehículo de 25-29 años está en el listado de bloqueo.
 * Devuelve:
 *  - "MATCH_TOTAL" → marca + modelo + versión encajan
 *  - "MATCH_MODELO_ALL" → marca + modelo encajan y se bloquean TODAS las versiones
 *  - "MATCH_MODELO_SIN_VERSION" → marca + modelo encajan pero no tenemos versión para confirmar
 *  - "NO_MATCH" → no hay coincidencia
 */
export function checkBloqueoList(
  marca?: string,
  modelo?: string,
  version?: string
): "MATCH_TOTAL" | "MATCH_MODELO_ALL" | "MATCH_MODELO_SIN_VERSION" | "NO_MATCH" {
  if (!marca || !modelo) return "NO_MATCH";

  const normMarca = normalize(marca);
  const normModelo = normalize(modelo);
  const normVersion = version ? normalize(version) : "";

  for (const entry of LISTADO_BLOQUEO_PRE_CLASICOS) {
    const entryMarca = normalize(entry.marca);
    const entryModelo = normalize(entry.modelo);

    // Marca debe coincidir
    if (!normMarca.includes(entryMarca) && !entryMarca.includes(normMarca)) continue;

    // Modelo debe coincidir
    if (!normModelo.includes(entryModelo) && !entryModelo.includes(normModelo)) continue;

    // Si versiones es null → "Todas las versiones"
    if (entry.versiones === null) return "MATCH_MODELO_ALL";

    // Si hay versiones específicas, comprobar si alguna coincide
    const textoCompleto = `${normModelo} ${normVersion}`;
    for (const v of entry.versiones) {
      const normV = normalize(v);
      if (textoCompleto.includes(normV)) return "MATCH_TOTAL";
    }

    // Marca y modelo coinciden pero no la versión, y no tenemos info de versión
    if (!normVersion) return "MATCH_MODELO_SIN_VERSION";
  }

  return "NO_MATCH";
}

/**
 * Clasificación completa con nivel de confianza.
 * Retorna tipo (CLASICO/PRE_CLASICO), confianza y razón.
 */
export function clasificarVehiculo(s: {
  tipoVehiculoKanban?: string;
  vehiculo_tipo?: string;
  vehiculo_ano?: string | number;
  vehiculo_marca?: string;
  vehiculo_modelo?: string;
  vehiculo_version?: string;
  edad_vehiculo?: string | number;
}): ClasificacionResult {
  // Campo explícito tiene prioridad absoluta (ya lo decidió un agente)
  if (s.tipoVehiculoKanban === "PRE_CLASICO") {
    return { tipo: "PRE_CLASICO", confianza: "ALTA", razon: "Clasificado manualmente como pre-clásico." };
  }
  if (s.tipoVehiculoKanban === "CLASICO") {
    return { tipo: "CLASICO", confianza: "ALTA", razon: "Clasificado manualmente como clásico." };
  }

  const tipo = (s.vehiculo_tipo || "").toLowerCase();

  // Motos → siempre clásico
  if (tipo.includes("moto")) {
    return { tipo: "CLASICO", confianza: "ALTA", razon: "Las motos son siempre clásicos." };
  }

  let antiguedad = 0;

  const ano = s.vehiculo_ano;
  const edad = s.edad_vehiculo;

  // edad_vehiculo (tramo oficial del sistema) tiene prioridad sobre vehiculo_ano.
  // vehiculo_ano puede contener el año de introducción del motor o una fecha de
  // fabricación errónea que no refleja el tramo real de tarificación.
  if (edad) {
    const edadVal = parseInt(String(edad).replace(/\D/g, ''), 10);
    if (!isNaN(edadVal) && edadVal > 0) antiguedad = edadVal;
  }

  if (!antiguedad && ano) {
    const year = parseInt(String(ano).replace(/\D/g, ''), 10);
    if (!isNaN(year)) antiguedad = new Date().getFullYear() - year;
  }

  if (!antiguedad || isNaN(antiguedad)) {
    return { tipo: "CLASICO", confianza: "REVISAR", razon: "Sin año ni edad de matriculación.", sugerenciaRevision: "Verificar el año o tramo del vehículo para clasificarlo correctamente." };
  }

  // < 20 años → no aplica (no debería estar aquí)
  if (antiguedad < 20) {
    return { tipo: "CLASICO", confianza: "REVISAR", razon: `Vehículo con ${antiguedad} años, menos de 20.`, sugerenciaRevision: "Este vehículo no cumple el mínimo de antigüedad." };
  }

  // 20-24 años → SIEMPRE pre-clásico
  if (antiguedad >= 20 && antiguedad <= 24) {
    return { tipo: "PRE_CLASICO", confianza: "ALTA", razon: `${antiguedad} años de antigüedad (franja 20-24). Siempre pre-clásico.` };
  }

  // 25-29 años → depende del listado
  if (antiguedad >= 25 && antiguedad <= 29) {
    const match = checkBloqueoList(s.vehiculo_marca, s.vehiculo_modelo, s.vehiculo_version);

    switch (match) {
      case "MATCH_TOTAL":
        return {
          tipo: "PRE_CLASICO",
          confianza: "ALTA",
          razon: `${s.vehiculo_marca} ${s.vehiculo_modelo} con versión bloqueada (${antiguedad} años). Confirmado pre-clásico.`
        };

      case "MATCH_MODELO_ALL":
        return {
          tipo: "PRE_CLASICO",
          confianza: "ALTA",
          razon: `${s.vehiculo_marca} ${s.vehiculo_modelo}: Todas las versiones de este modelo están bloqueadas (${antiguedad} años).`
        };

      case "MATCH_MODELO_SIN_VERSION":
        return {
          tipo: "PRE_CLASICO",
          confianza: "REVISAR",
          razon: `${s.vehiculo_marca} ${s.vehiculo_modelo} está en el listado pero no tenemos la versión para confirmar.`,
          sugerenciaRevision: `Este modelo tiene versiones bloqueadas. Comprobar si es una de ellas: consultar ficha técnica o web del fabricante.`
        };

      case "NO_MATCH":
        return {
          tipo: "CLASICO",
          confianza: "MEDIA",
          razon: `${s.vehiculo_marca} ${s.vehiculo_modelo} (${antiguedad} años) no aparece en el listado de bloqueo.`
        };
    }
  }

  // 30+ años → siempre clásico
  return { tipo: "CLASICO", confianza: "ALTA", razon: `${antiguedad} años de antigüedad (30+). Siempre clásico.` };
}

/**
 * Wrapper simple: ¿Es pre-clásico? (true/false)
 * Para uso en filtros donde no necesitas la confianza.
 */
export function isPreClasico(s: {
  tipoVehiculoKanban?: string;
  vehiculo_tipo?: string;
  vehiculo_ano?: string | number;
  vehiculo_marca?: string;
  vehiculo_modelo?: string;
  vehiculo_version?: string;
}): boolean {
  const result = clasificarVehiculo(s);
  return result.tipo === "PRE_CLASICO";
}

